import { useCallback, useRef, useState } from 'react';
import Cropper from 'react-easy-crop';
import { Image as ImageIcon } from 'lucide-react';
import { Button } from '@techsofit/ui';
import { uploadCoverImage } from '../lib/adminApi';
import { getCroppedBlob, type CropArea } from '../lib/cropImage';

/**
 * Cover-image field: pick a file, crop it to the exact aspect it'll display at, then
 * upload the cropped result to Azure. `value` is the stored URL; `onChange(url|null)`.
 */
export function ImageUpload({
  value,
  onChange,
  aspect = 16 / 9,
  label = 'Cover image',
}: {
  value: string | null;
  onChange: (url: string | null) => void;
  aspect?: number;
  label?: string;
}) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [src, setSrc] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [area, setArea] = useState<CropArea | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function pick(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => {
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setError(null);
      setSrc(reader.result as string);
    };
    reader.readAsDataURL(f);
  }

  const onCropComplete = useCallback((_a: CropArea, px: CropArea) => setArea(px), []);

  async function save() {
    if (!src || !area) return;
    setBusy(true);
    setError(null);
    try {
      const blob = await getCroppedBlob(src, area);
      const { url } = await uploadCoverImage(blob);
      onChange(url);
      setSrc(null);
    } catch {
      setError('Upload failed. Try again.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <p className="mb-1.5 text-sm font-medium text-ink-soft">{label}</p>
      {value ? (
        <div className="relative w-full max-w-sm overflow-hidden rounded-control border border-line">
          <img src={value} alt="" className="aspect-video w-full object-cover" />
          <div className="absolute right-2 top-2 flex gap-2">
            <Button size="sm" variant="dark" onClick={() => fileRef.current?.click()}>Change</Button>
            <Button size="sm" variant="ghost" className="bg-card" onClick={() => onChange(null)}>Remove</Button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          className="flex aspect-video w-full max-w-sm flex-col items-center justify-center gap-2 rounded-control border-2 border-dashed border-line-strong text-muted transition hover:bg-surface hover:text-ink"
        >
          <ImageIcon className="size-6" aria-hidden />
          <span className="text-sm font-medium">Upload &amp; crop an image</span>
        </button>
      )}
      <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={pick} />

      {src && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-ink/60 p-4 backdrop-blur-sm">
          <div className="w-full max-w-lg rounded-card bg-card p-4 shadow-pop">
            <p className="text-sm font-medium text-ink">Crop to fit</p>
            <div className="relative mt-3 h-72 w-full overflow-hidden rounded-control bg-dark">
              <Cropper
                image={src}
                crop={crop}
                zoom={zoom}
                aspect={aspect}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
              />
            </div>
            <div className="mt-3 flex items-center gap-3">
              <span className="text-xs text-muted">Zoom</span>
              <input type="range" min={1} max={3} step={0.01} value={zoom} onChange={(e) => setZoom(Number(e.target.value))} className="flex-1" />
            </div>
            {error && <p className="mt-2 text-sm font-medium text-bad-ink">{error}</p>}
            <div className="mt-3 flex justify-end gap-2">
              <Button size="sm" variant="ghost" onClick={() => setSrc(null)} disabled={busy}>Cancel</Button>
              <Button size="sm" onClick={save} loading={busy}>Save image</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
